'use client';

import { useEffect, useRef, useState } from 'react';

type CarouselEnhancerProps = {
  rootId: string;
  trackId: string;
  slideCount: number;
  enableArrows: boolean;
  enableDots: boolean;
  snapAlign: 'start' | 'center';
};

type Cleanup = () => void;

type SlideMeasurement = {
  positions: number[];
  maxScrollLeft: number;
};

function measureSlides(
  track: HTMLUListElement,
  slides: HTMLElement[],
): SlideMeasurement {
  const maxScrollLeft = Math.max(0, track.scrollWidth - track.clientWidth);
  const positions = slides.map((slide) =>
    Math.min(slide.offsetLeft, maxScrollLeft),
  );

  return { positions, maxScrollLeft };
}

function findNearestIndex(
  current: number,
  positions: number[],
  snapAlign: 'start' | 'center',
  track: HTMLUListElement,
): number {
  if (!positions.length) return 0;
  const bias = snapAlign === 'center' ? track.clientWidth / 2 : 0;
  const point = current + bias;
  let nearest = 0;
  let minDist = Number.POSITIVE_INFINITY;

  for (let index = 0; index < positions.length; index += 1) {
    const dist = Math.abs(point - positions[index]);
    if (dist < minDist) {
      nearest = index;
      minDist = dist;
    }
  }

  return nearest;
}

export function CarouselEnhancer({
  rootId,
  trackId,
  slideCount,
  enableArrows,
  enableDots,
  snapAlign,
}: CarouselEnhancerProps) {
  const [shouldEnhance, setShouldEnhance] = useState(false);
  const cleanupRef = useRef<Cleanup | null>(null);

  useEffect(() => {
    const root = document.getElementById(rootId);
    if (!root || slideCount <= 1) {
      // Nothing to enhance if we cannot find the markup or there is a single slide.
      return undefined;
    }

    if (shouldEnhance) {
      return undefined;
    }

    let cancelled = false;

    const triggerEnhancement = () => {
      if (cancelled) return;
      cancelled = true;
      setShouldEnhance(true);
    };

    const interactionEvents: Array<{
      name: keyof HTMLElementEventMap;
      options?: AddEventListenerOptions;
    }> = [
      { name: 'pointerdown', options: { once: true, passive: true } },
      { name: 'keydown', options: { once: true } },
      { name: 'focusin', options: { once: true } },
    ];

    for (const { name, options } of interactionEvents) {
      root.addEventListener(name, triggerEnhancement, options);
    }

    let observer: IntersectionObserver | null = null;

    if ('IntersectionObserver' in globalThis) {
      observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              triggerEnhancement();
              break;
            }
          }
        },
        { rootMargin: '160px 0px' },
      );
      observer.observe(root);
    } else {
      // No IntersectionObserver support, hydrate immediately.
      triggerEnhancement();
    }

    return () => {
      cancelled = true;
      if (observer) {
        observer.disconnect();
      }
      for (const { name } of interactionEvents) {
        root.removeEventListener(name, triggerEnhancement);
      }
    };
  }, [rootId, slideCount, shouldEnhance]);

  useEffect(() => {
    if (!shouldEnhance || slideCount <= 1) {
      return undefined;
    }

    const root = document.getElementById(rootId);
    const track = document.getElementById(trackId) as HTMLUListElement | null;

    if (!root || !track) {
      return undefined;
    }

    const slideElements = Array.from(
      track.querySelectorAll<HTMLElement>('[data-carousel-slide]'),
    );

    if (slideElements.length <= 1) {
      return undefined;
    }

    const navWrapper = enableArrows
      ? root.querySelector<HTMLElement>('[data-carousel-nav]')
      : null;
    const prevButton = navWrapper
      ? navWrapper.querySelector<HTMLButtonElement>('[data-carousel-prev]')
      : null;
    const nextButton = navWrapper
      ? navWrapper.querySelector<HTMLButtonElement>('[data-carousel-next]')
      : null;

    const dotsWrapper = enableDots
      ? root.querySelector<HTMLElement>('[data-carousel-dots]')
      : null;
    const dotButtons = dotsWrapper
      ? Array.from(
          dotsWrapper.querySelectorAll<HTMLButtonElement>(
            '[data-carousel-dot]',
          ),
        )
      : [];

    const dotIndicators = dotsWrapper
      ? Array.from(
          dotsWrapper.querySelectorAll<HTMLElement>(
            '[data-carousel-dot-indicator]',
          ),
        )
      : [];

    if (navWrapper) {
      navWrapper.classList.remove('hidden');
      navWrapper.dataset.enhanced = 'true';
    }
    if (dotsWrapper) {
      dotsWrapper.classList.remove('hidden');
      dotsWrapper.dataset.enhanced = 'true';
      dotsWrapper.setAttribute('aria-hidden', 'false');
    }

    let rafHandle: number | null = null;
    let resizeHandle: number | null = null;
    let activeIndex = 0;

    const state = {
      positions: [] as number[],
      maxScrollLeft: 0,
    };

    const applyActiveState = (index: number) => {
      activeIndex = index;

      if (prevButton) {
        prevButton.disabled = index <= 0;
      }
      if (nextButton) {
        nextButton.disabled = index >= slideElements.length - 1;
      }

      for (const [buttonIndex, button] of dotButtons.entries()) {
        const isActive = buttonIndex === index;
        button.dataset.active = isActive ? 'true' : 'false';
        if (isActive) {
          button.setAttribute('aria-current', 'true');
        } else {
          button.removeAttribute('aria-current');
        }
      }

      for (const [indicatorIndex, indicator] of dotIndicators.entries()) {
        const isActive = indicatorIndex === index;
        indicator.dataset.active = isActive ? 'true' : 'false';
      }
    };

    const updateMeasurements = () => {
      const measurements = measureSlides(track, slideElements);
      state.positions = measurements.positions;
      state.maxScrollLeft = measurements.maxScrollLeft;
    };

    const updateActiveFromScroll = () => {
      const nextIndex = findNearestIndex(
        track.scrollLeft,
        state.positions,
        snapAlign,
        track,
      );
      applyActiveState(nextIndex);
    };

    updateMeasurements();
    updateActiveFromScroll();

    const handleScroll = () => {
      if (rafHandle !== null) {
        return;
      }
      rafHandle = globalThis.requestAnimationFrame(() => {
        rafHandle = null;
        updateActiveFromScroll();
      });
    };

    const goToIndex = (index: number) => {
      const clamped = Math.max(0, Math.min(index, state.positions.length - 1));
      const offset = Math.min(state.positions[clamped], state.maxScrollLeft);
      track.scrollTo({ left: offset, behavior: 'smooth' });
    };

    const handlePrev = () => {
      goToIndex(activeIndex - 1);
    };

    const handleNext = () => {
      goToIndex(activeIndex + 1);
    };

    const handleDotClick = (event: MouseEvent) => {
      const target = event.currentTarget as HTMLButtonElement;
      const value = Number.parseInt(String(target.dataset.carouselDot), 10);
      if (!Number.isNaN(value)) {
        goToIndex(value);
      }
    };

    track.addEventListener('scroll', handleScroll, { passive: true });
    prevButton?.addEventListener('click', handlePrev);
    nextButton?.addEventListener('click', handleNext);
    for (const button of dotButtons) {
      button.addEventListener('click', handleDotClick);
    }

    const resizeObserver = new ResizeObserver(() => {
      if (resizeHandle !== null) {
        cancelAnimationFrame(resizeHandle);
      }
      resizeHandle = globalThis.requestAnimationFrame(() => {
        resizeHandle = null;
        updateMeasurements();
        updateActiveFromScroll();
      });
    });

    resizeObserver.observe(track);

    cleanupRef.current = () => {
      if (rafHandle !== null) {
        cancelAnimationFrame(rafHandle);
      }
      if (resizeHandle !== null) {
        cancelAnimationFrame(resizeHandle);
      }
      track.removeEventListener('scroll', handleScroll);
      prevButton?.removeEventListener('click', handlePrev);
      nextButton?.removeEventListener('click', handleNext);
      for (const button of dotButtons) {
        button.removeEventListener('click', handleDotClick);
      }
      resizeObserver.disconnect();
    };

    return cleanupRef.current;
  }, [
    enableArrows,
    enableDots,
    rootId,
    shouldEnhance,
    slideCount,
    snapAlign,
    trackId,
  ]);

  useEffect(
    () => () => {
      if (cleanupRef.current) {
        cleanupRef.current();
      }
    },
    [],
  );

  return null;
}
